
'use client'

import { Button } from "@/components/ui/button"
import { SimpleProduct } from "@/types/product"
import { useTranslations } from 'next-intl'
import { useRTLAwareStyle } from "@/util/rtl"
import Link from "next/link"
import AnimatedImage from "../AnimatedImage"
import { Badge } from "@/components/ui/badge"

interface ProductCardProps {
  product: SimpleProduct
  lang: "en" | "ar"
}

export default function ProductCard({ product, lang }: ProductCardProps) {
  const t = useTranslations('product')
  const rtlAwareStyle = useRTLAwareStyle('left-0', 'right-0')
  const letterSpacing = useRTLAwareStyle('tracking-widest', '')

  const discountPercentage = product.previousPrice 
    ? Math.round(((product.previousPrice - product.price) / product.previousPrice) * 100) 
    : 0

  return (
    <Link 
      href={`/${lang}/products/${product.slug}`} 
      passHref 
      className="block group relative w-full overflow-hidden border border-transparent transition-all duration-300 ease-in-out hover:border-gray-300 hover:shadow-lg"
    >
      {discountPercentage > 0 && (
        <Badge className="absolute top-4 left-4 z-10 bg-red-600 hover:bg-red-700 font-roboto">
          {t('discount', { percentage: discountPercentage })}
        </Badge>
      )}
      <div className="relative w-full h-[200px] md:h-[450px] overflow-hidden">
        <AnimatedImage 
          thumbnailSrc={product.image.thumbnail}
          mainSrc={product.image.url}
          hoverSrc={product.secondImage?.url} // Add the secondary image URL here
          alt={product.image.altText}
          sizes="(max-width: 768px) 100vw, 400px"
          quality={100}
          className="w-full h-full transition-transform duration-300 ease-in-out"
        />
      </div>
      <div className="p-2 md:p-4 text-center">
        <div className="h-[48px] md:h-[56px] flex items-center justify-center">
          <h3 className={`text-lg md:text-xl font-bold ${letterSpacing} line-clamp-2`}>
            {product.name}
          </h3>
        </div>
        <div className="mt-2 flex flex-col items-center gap-2">
          <div className="flex items-center gap-2 justify-center">
            <span className="text-base !font-roboto">
              {t('from')} {t('currency', { amount: product.price.toFixed(2) })}
            </span>
            {product.previousPrice && (
              <span className="text-sm text-gray-500 line-through !font-roboto">
                {t('currency', { amount: product.previousPrice.toFixed(2) })}
              </span>
            )}
          </div>
          <Button 
            className={`w-full rounded-none bg-darGreyy text-white transition-colors duration-300 ease-in-out md:bg-transparent md:text-darGreyy md:group-hover:bg-darGreyy md:group-hover:text-white ${letterSpacing}`}
          >
            {t('moreDetails')}
          </Button>
        </div>
      </div>
    </Link>
  )
}